import { getBookMove, levelToElo, levelToDepth } from '@chess-platform/shared';

/**
 * M2: Capa de Juego con Selector de Capas (Maia + Stockfish + Syzygy).
 */
class AiEngine {
    private worker: Worker | null = null;
    private isReady: boolean = false;

    constructor() {
        this.init();
    }

    public init() {
        if (typeof window === 'undefined' || this.worker) return;
        this.worker = new Worker('/stockfish.js');
        this.worker.onmessage = (e) => {
            if (e.data === 'readyok') this.isReady = true;
        };
        this.worker.postMessage('uci');
        this.worker.postMessage('isready');
    }

    private async getSyzygyMove(fen: string): Promise<string | null> {
        try {
            // Consulta a la Base de Datos de Finales Syzygy (Lichess API)
            const res = await fetch(`https://tablebase.lichess.ovh/standard?fen=${encodeURIComponent(fen)}`);
            if (res.ok) {
                const data = await res.json();
                if (data.moves && data.moves.length > 0) {
                    console.info('[Syzygy] Jugada de finales perfecta encontrada.');
                    return data.moves[0].uci;
                }
            }
        } catch (e) {
            console.warn('[Syzygy] No disponible, usando cálculo local.');
        }
        return null;
    }

    private async getMaiaMove(fen: string, level: number): Promise<string | null> {
        // Mapeo de nivel a motor Maia (1100 a 1900 ELO)
        const elo = levelToElo(level);
        const maiaLevel = Math.max(1100, Math.min(1900, Math.floor(elo / 100) * 100));

        try {
            console.info(`[Maia] Consultando personalidad humana ELO ${maiaLevel}...`);
            // Nota: En producción esto iría a un servicio Maia, aquí simulamos 
            // la probabilidad humana o usamos la API de Lichess Cloud si está disponible
            return null;
        } catch (e) { return null; }
    }

    public async getBestMove(fen: string, level: number, moveNumber: number): Promise<string | null> {
        // 1. Selector: ¿Es un final de 7 piezas o menos? -> Syzygy
        const pieceCount = (fen.match(/[prnbqkPRNBQK]/g) || []).length;
        if (pieceCount <= 7) {
            const syzygyMove = await this.getSyzygyMove(fen);
            if (syzygyMove) return syzygyMove;
        }

        // 2. Selector: Apertura -> Libro de jugadas
        const bookMove = getBookMove(fen, level, moveNumber);
        if (bookMove) return bookMove;

        // 3. Selector: Medio juego -> Maia (Humano) o Stockfish (Matemático)
        // Si el nivel es bajo/medio (< 2000 ELO), preferimos Maia
        if (level < 18) {
            const maiaMove = await this.getMaiaMove(fen, level);
            if (maiaMove) return maiaMove;
        }

        // 4. Fallback: Stockfish para precisión pura
        return new Promise((resolve) => {
            if (!this.worker) return resolve(null);

            const depth = levelToDepth(level);
            let resolved = false;

            const handler = (e: MessageEvent) => {
                const msg = e.data as string;
                if (msg.startsWith('bestmove')) {
                    if (resolved) return;
                    resolved = true;
                    this.worker?.removeEventListener('message', handler);
                    const move = msg.split(' ')[1];
                    resolve(move && move !== '(none)' ? move : null);
                }
            };

            // Limit calculation time strictly
            // Level 20 = 5 seconds max (high quality but fast)
            // Lower levels = 2-3 seconds
            const maxWait = level >= 20 ? 5000 : 3000;

            const timeout = setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    this.worker?.removeEventListener('message', handler);
                    this.worker?.postMessage('stop');
                    // If stop doesn't trigger bestmove immediately, we'll give it a tiny bit more
                    setTimeout(() => resolve(null), 100);
                }
            }, maxWait);

            this.worker.addEventListener('message', handler);

            this.worker.postMessage(`setoption name Skill Level value ${Math.min(20, level)}`);
            this.worker.postMessage(`position fen ${fen}`);

            // For level 20, we use a time limit instead of depth to ensure responsiveness
            if (level >= 20) {
                this.worker.postMessage(`go movetime ${maxWait - 500}`);
            } else {
                this.worker.postMessage(`go depth ${depth}`);
            }
        });
    }
}

export const aiEngine = new AiEngine();
